import { useClient, useQuery } from '@weenat/client'
import { getTimeDomain } from '@weenat/client/dist/core/charts'
import {
  AllowedMetricsInHistory,
  HistoryPeriod,
  getMetricsToFetch
} from '@weenat/client/dist/core/history/history'
import { getYDomainPerMetric } from '@weenat/client/dist/core/measurements'
import { TimeSteps } from '@weenat/client/dist/enums/TimeSteps'
import { useConvertedValue } from '@weenat/client/dist/hooks'
import { Id } from '@weenat/client/dist/resources/types'
import { fromColorPathToColor } from '@weenat/theme'
import { useIntl } from '@weenat/wintl'
import { extent } from 'd3-array'
import { isEmpty, isNil } from 'lodash-es'
import { transparentize } from 'polished'
import { useMemo, useRef } from 'react'
import Card from '../kit/Card'
import ChartLegendItem from '../kit/charts/ChartLegendItem'
import DelimitedFlex from '../kit/DelimitedFlex'
import EmptyStateWithRipple from '../kit/empty-state/EmptyStateWithRipple'
import Icons from '../kit/Icons'
import MetricIcons from '../kit/icons/MetricIcons'
import LoadingCircle from '../kit/loaders/LoadingCircle'
import Text from '../kit/Text'
import DeviceMetricChart from './DeviceMetricChart'
import PeriodBasedMetricDigest from './PeriodBasedMetricDigest'

interface DeviceMetricCardProps {
  /**
   * Id of the device to fetch data for.
   * Volontarily omitting stations, having a station means having access to the device.
   */
  deviceId: Id
  isVWS: boolean
  metric: AllowedMetricsInHistory
  /**
   * When provided, the card is controlled by an external period.
   * The per-card period selector is hidden in that case.
   */
  period: HistoryPeriod
  /** Time-step paired with the controlled period. Defaults to hourly. */
  timeStep: TimeSteps
}

const DeviceMetricCard: FC<DeviceMetricCardProps> = ({
  metric,
  period,
  timeStep,
  deviceId,
  isVWS
}) => {
  const { t } = useIntl()
  const client = useClient()
  const { getUnit } = useConvertedValue()
  const unit = getUnit(metric)

  const MetricIcon = MetricIcons[metric]

  const chartRef = useRef<HTMLDivElement>(null)

  const metrics = getMetricsToFetch([metric])

  const dataQuery = useQuery(
    client.charts.history({
      deviceId: deviceId,
      timeStep,
      since: period.since,
      before: period.before,
      metrics,
      soilTexture: null,
      isAggregateBySerial: false,
      isAggregateByMetric: false,
      highlightThresholds: false,
      base: 0,
      canUseVPDOrTHIFromDevice: false
    })
  )

  const dataByMetric = dataQuery.data?.dataByMetric
  const dataByDateByMetric = dataQuery.data?.dataByDateByMetric

  const data = dataByMetric?.get(metric)

  const values = useMemo(() => data?.map((dp) => dp.value) ?? [], [data])
  const nonNullValues = useMemo(() => values.filter((val) => !isNil(val)), [values])

  const xDomain = useMemo(() => {
    return !isNil(data) && !isEmpty(data) ? getTimeDomain(data) : undefined
  }, [data])

  const yDomain = useMemo(() => {
    const validValues = !isNil(values) ? values.filter((v): v is number => !isNil(v)) : []

    let yExtent = !isEmpty(validValues) ? extent(validValues) : null

    if (metric === 'FF') {
      const gusts = dataByMetric
        ?.get('FXY')
        ?.map((dp) => dp.value)
        ?.filter((v): v is number => !isNil(v))

      if (!isNil(gusts)) {
        const gustsExtent = !isEmpty(validValues) ? extent(gusts) : undefined

        if (!isNil(gustsExtent)) {
          yExtent = [
            Math.min(gustsExtent.at(0), yExtent?.at(0)),
            Math.max(gustsExtent.at(1), yExtent?.at(1))
          ]
        }
      }
    }

    const domain = getYDomainPerMetric({
      metric,
      unit,
      timeStep,
      min: yExtent?.at(0),
      max: yExtent?.at(1)
    })
    return domain
  }, [dataByMetric, metric, timeStep, unit, values])

  const domain = useMemo(() => {
    if (isNil(xDomain) || isNil(yDomain)) return undefined

    return {
      x: xDomain,
      y: yDomain
    }
  }, [xDomain, yDomain])

  return (
    <Box $width={'100%'}>
      <Card $p={0} $overflow='hidden'>
        {/* HEADER */}
        <DelimitedFlex $p={'md'} $alignItems='center' $gap='md' $pr='md' $flex={1}>
          <MetricIcon
            $size='lg'
            $backgroundColor={`metrics.${metric}.500`}
            $p='sm'
            $color='grayscale.white'
          />
          <Flex $flex={1} $minWidth={0}>
            <Text $fontWeight='bold' $ellipsis>
              {t(`metrics.${metric as 'T'}`, { capitalize: true })}
            </Text>
          </Flex>
        </DelimitedFlex>

        {/* CONTENT */}
        <Flex $flexDirection='column'>
          {dataQuery.isLoading ? (
            <Flex $justifyContent='center' $alignItems='center' $p='md' $height={196}>
              <LoadingCircle />
            </Flex>
          ) : dataQuery.isError || isNil(dataQuery) || isNil(domain) ? (
            <Flex $justifyContent='center' $alignItems='center' $p='md' $height={196}>
              <Text $textAlign='center'>{t('errors.unknown')}</Text>
            </Flex>
          ) : dataQuery.isSuccess && isEmpty(nonNullValues) ? (
            <Flex $justifyContent='center' $alignItems='center' $p='md' $height={196}>
              <EmptyStateWithRipple Icon={Icons.BarChart} description={t('charts.no_data')} />
            </Flex>
          ) : dataQuery.isSuccess ? (
            <>
              <DelimitedFlex $width={'100%'} $pb={'sm'} $flexDirection='column'>
                {!isNil(dataByMetric) && !isNil(dataByDateByMetric) ? (
                  <DeviceMetricChart
                    chartRef={chartRef}
                    isVWS={isVWS}
                    dataByMetric={dataByMetric}
                    dataByDateByMetric={dataByDateByMetric}
                    domain={domain}
                    timeStep={timeStep}
                    metric={metric}
                  />
                ) : null}
              </DelimitedFlex>
              {metric === 'FF' ? (
                <DelimitedFlex
                  $py={'sm'}
                  $px={'600'}
                  $gap={'md'}
                  $flexDirection='row'
                  $flexWrap='wrap'
                  $justifyContent='center'
                >
                  <ChartLegendItem
                    variant='dot'
                    color={`metrics.FF.500`}
                    label={t(`metrics.FF_abbr`, { capitalize: true })}
                    labelSize={'sm'}
                  />
                  <ChartLegendItem
                    variant='dot'
                    color={transparentize(0.5)(fromColorPathToColor(`metrics.FF.500`))}
                    label={t(`metrics.FXY_abbr`, { capitalize: true })}
                    labelSize={'sm'}
                  />
                </DelimitedFlex>
              ) : null}

              <PeriodBasedMetricDigest
                data={data}
                metric={metric}
                period={period}
                timeStep={timeStep}
                deviceId={deviceId}
              />
            </>
          ) : null}
        </Flex>
      </Card>
    </Box>
  )
}

export default DeviceMetricCard
